import { ILinkingFailure, isTerminalLinkingReason } from './types'
import { markEntitlementConsumed } from './entitlementStore'

/**
 * In-memory channel between `withEntitlement` (which observes the linking
 * outcome inside an auth mutation) and the AuthActor modal that surfaces it
 * (PRO-15). Deliberately NOT persisted: the message is about the auth that
 * just ran on this page — a reload lands on the regular logged-in experience,
 * and the kept/tombstoned record in `entitlementStore` already encodes
 * whether a later auth should retry. Module state + subscribers rather than a
 * Zustand store: `CinematicsStore` is meant to stay the only global store,
 * and `useSyncExternalStore` covers this one value.
 */
let currentFailure: ILinkingFailure | null = null
const listeners = new Set<() => void>()

const notify = () => {
  listeners.forEach((listener) => listener())
}

export const getLinkingFailure = (): ILinkingFailure | null => currentFailure

export const setLinkingFailure = (failure: ILinkingFailure): void => {
  currentFailure = failure
  notify()
}

export const clearLinkingFailure = (): void => {
  if (currentFailure === null) return
  currentFailure = null
  notify()
}

export const subscribeLinkingFailure = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The user has *acknowledged* the pending failure (clicked any button on the
 * modal): a terminal reason tombstones the record now — informed once, the
 * dead id must stop riding and re-failing every auth for the rest of the TTL.
 * Deliberately separate from `clearLinkingFailure`: clearing only removes the
 * surface (unmount, embedded suppression) and must never consume the record,
 * or a failure the user never saw would silently kill the retry that existed
 * before PRO-15. Retryable reasons consume nothing here either — their record
 * stays for the next auth by design.
 */
export const consumeLinkingFailure = (): void => {
  if (currentFailure && isTerminalLinkingReason(currentFailure.reason)) {
    markEntitlementConsumed(currentFailure.entitlementId)
  }
}
